import React, { useEffect, useState, useRef } from 'react';
import { PROCESSING_MESSAGES } from '@/lib/processing-messages';

interface ProcessingNarrativeProps {
  /**
   * Optional video preview URL to display
   */
  videoPreviewUrl?: string | null;
  /**
   * Optional className for styling
   */
  className?: string;
}

/**
 * ProcessingNarrative Component
 * 
 * Displays V3-aligned scrolling status messages during analysis processing.
 * Shows video preview if available, with intentional, slow scrolling of status updates.
 * 
 * Features:
 * - Vertically scrolling list of status messages
 * - Messages grouped by pipeline stage
 * - Slow, intentional scrolling (not frantic)
 * - Loops messages if processing takes longer
 * - No progress bars, percentages, or timers
 */
export function ProcessingNarrative({ videoPreviewUrl, className }: ProcessingNarrativeProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentStage, setCurrentStage] = useState<keyof typeof PROCESSING_MESSAGES>('early');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);

  // Flatten all messages in order: early -> analysis -> interpretation
  const allMessages = [
    ...PROCESSING_MESSAGES.early,
    ...PROCESSING_MESSAGES.analysis,
    ...PROCESSING_MESSAGES.interpretation,
  ];

  // Determine current stage based on message index
  useEffect(() => {
    const earlyCount = PROCESSING_MESSAGES.early.length;
    const analysisCount = PROCESSING_MESSAGES.analysis.length;
    
    if (currentMessageIndex < earlyCount) {
      setCurrentStage('early');
    } else if (currentMessageIndex < earlyCount + analysisCount) {
      setCurrentStage('analysis');
    } else {
      setCurrentStage('interpretation');
    }
  }, [currentMessageIndex]);

  // Scroll to current message
  useEffect(() => {
    if (scrollContainerRef.current) {
      const messageElement = scrollContainerRef.current.children[currentMessageIndex] as HTMLElement;
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [currentMessageIndex]);

  // Cycle through messages slowly
  useEffect(() => {
    // Change message every 3 seconds (slow, intentional)
    intervalRef.current = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        const next = prev + 1;
        // Loop back to start if we've shown all messages
        return next >= allMessages.length ? 0 : next;
      });
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className={className}>
      {/* Video Preview (if available) */}
      {videoPreviewUrl && (
        <div className="mb-6 rounded-2xl overflow-hidden bg-black aspect-video shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
          <video
            src={videoPreviewUrl}
            muted
            loop
            playsInline
            autoPlay
            className="w-full h-full object-contain"
            aria-label="Video preview"
          />
        </div>
      )}

      {/* Processing Content */}
      <div className="bg-white rounded-2xl p-8 shadow-[0_2px_8px_rgba(13,56,42,0.08)] text-center">
        {/* Processing Headline */}
        <h3 className="text-xl font-semibold text-[#0D382A] mb-6">
          Analyzing your swing
        </h3>

        {/* Scrolling Status Narrative */}
        <div
          ref={scrollContainerRef}
          className="h-32 overflow-hidden relative mb-4"
          role="log"
          aria-live="polite"
          aria-label="Processing status updates"
        >
          {allMessages.map((message, index) => (
            <div
              key={index}
              className={`text-gray-600 text-sm py-2 transition-opacity duration-500 ${
                index === currentMessageIndex
                  ? 'opacity-100 font-medium text-[#196644]'
                  : 'opacity-40'
              }`}
            >
              {message}
            </div>
          ))}
        </div>

        {/* Subtle Reassurance */}
        <p className="text-sm text-gray-500">
          This usually takes a moment
        </p>
      </div>
    </div>
  );
}

